import { formatCuit } from './Masks';
import { formatDocumentForSearch } from '../organisms/Origination/formatDocumentForSearch';
import type { TenantCountry } from './countryConfig';

export type FiscalIdConfig = {
  country: 'AR' | 'UY';
  label: string;
  cuilLabel: string;
  drawerLabel: string;
  capLabel: string;
  availableShortLabel: string;
  availableCurrencyLabel: string;
  searchPlaceholder: string;
  filterPlaceholder: string;
  companySearchPlaceholder: string;
  guaranteeSearchPlaceholder: string;
  enterPlaceholder: string;
  requiredMessage: string;
  invalidLengthMessage: string;
  invalidMessage: string;
  missingIdOrApplicationMessage: string;
  mismatchMessage: string;
  format: (value?: string) => string;
  isValidDigits: (value: string) => boolean;
  mismatchWithValues: (balanceId: string, folderId: string) => string;
};

const AR_VALID_LENGTH = 11;

const isValidArCuitDigits = (value: string): boolean =>
  value.replace(/\D/g, '').length === AR_VALID_LENGTH;

const isValidUyFiscalDigits = (value: string): boolean => {
  const len = value.replace(/\D/g, '').length;
  return len === 12 || len === 7 || len === 8;
};

const FISCAL_ID_CONFIG: Record<'AR' | 'UY', FiscalIdConfig> = {
  AR: {
    country: 'AR',
    label: 'CUIT',
    cuilLabel: 'CUIT/CUIL',
    drawerLabel: 'CUIT Librador',
    capLabel: 'Linea CUIT',
    availableShortLabel: 'Disp. CUIT',
    availableCurrencyLabel: 'Disp. Linea Cliente',
    searchPlaceholder: 'Buscar razón social o CUIT',
    filterPlaceholder: 'Buscar por CUIT',
    companySearchPlaceholder: 'Buscar por CUIT o razón social',
    guaranteeSearchPlaceholder: 'Buscar por CUIT o Nº garantía',
    enterPlaceholder: 'Ingresá CUIT (ej: 20-12345678-9)',
    requiredMessage: 'Ingrese el CUIT',
    invalidLengthMessage: 'El CUIT debe tener 11 dígitos',
    invalidMessage: 'CUIT inválido',
    missingIdOrApplicationMessage: 'CUIT o ID de aplicación no disponible',
    mismatchMessage: 'El CUIT del balance no coincide con el de la carpeta.',
    format: (value) => formatCuit(value || ''),
    isValidDigits: isValidArCuitDigits,
    mismatchWithValues: (balanceId, folderId) =>
      `El CUIT del balance (${balanceId}) no coincide con el de la carpeta (${folderId}).`,
  },
  UY: {
    country: 'UY',
    label: 'RUT/Cédula',
    cuilLabel: 'RUT/Cédula',
    drawerLabel: 'RUT/Cédula librador',
    capLabel: 'Linea RUT/Cedula',
    availableShortLabel: 'Disp. RUT/Cédula',
    availableCurrencyLabel: 'Disp. Linea Cliente',
    searchPlaceholder: 'Buscar razón social o RUT/Cédula',
    filterPlaceholder: 'Buscar por RUT/Cédula',
    companySearchPlaceholder: 'Buscar por RUT/Cédula o razón social',
    guaranteeSearchPlaceholder: 'Buscar por RUT/Cédula o Nº garantía',
    enterPlaceholder: 'Ingresá RUT/Cédula',
    requiredMessage: 'Ingrese el RUT/Cédula',
    invalidLengthMessage: 'El RUT/Cédula debe tener 7, 8 o 12 dígitos',
    invalidMessage: 'RUT/Cédula inválido',
    missingIdOrApplicationMessage: 'RUT/Cédula o ID de aplicación no disponible',
    mismatchMessage: 'El RUT/Cédula del balance no coincide con el de la carpeta.',
    format: (value) => formatDocumentForSearch(value || '', true),
    isValidDigits: isValidUyFiscalDigits,
    mismatchWithValues: (balanceId, folderId) =>
      `El RUT/Cédula del balance (${balanceId}) no coincide con el de la carpeta (${folderId}).`,
  },
};

export function getFiscalIdConfig(country?: TenantCountry | string | null): FiscalIdConfig {
  const value = typeof country === 'string' ? country.trim().toUpperCase() : '';
  return FISCAL_ID_CONFIG[value === 'URUGUAY' || value === 'UY' ? 'UY' : 'AR'];
}
